import React from "react";
import InfoTooltip from "../common/InfoTooltip";
import { formatDateShort } from "../../utils/formatters";

export interface FreshnessLag {
  /** The lagging source in its own words, e.g. "Orders through Oct 1". */
  label: string;
  /** Why it ends there, shown in the navy tooltip. */
  tip: string;
}

export interface FreshnessStampProps {
  /** The newest fully loaded day (ISO date) — from the server, never the clock. */
  through?: string | null;
  /** Why the data ends on that day. Optional. */
  throughTip?: string;
  /** Sources that end before `through` name their own date. */
  lags?: FreshnessLag[];
}

/**
 * FreshnessStamp — the top bar's "Data through Oct 3" (handoff ruling 7):
 * one data-through day, plus each lagging source naming its own date.
 * No `through` → renders NOTHING. Never a placeholder date.
 */
const FreshnessStamp: React.FC<FreshnessStampProps> = ({ through, throughTip, lags = [] }) => {
  if (through == null || through === "") return null;
  const main = <span>Data through {formatDateShort(through)}</span>;
  return (
    <span
      data-testid="freshness-stamp"
      className="flex min-w-0 flex-wrap items-center gap-x-1.5 whitespace-nowrap rounded-full border border-line bg-white px-2.5 py-0.75 text-micro text-ink-muted"
    >
      {throughTip ? <InfoTooltip content={throughTip} position="bottom">{main}</InfoTooltip> : main}
      {lags.map((lag) => (
        <React.Fragment key={lag.label}>
          <span aria-hidden="true" className="text-gray-300">·</span>
          <InfoTooltip content={lag.tip} position="bottom">
            <span className="text-brand-navy">{lag.label}</span>
          </InfoTooltip>
        </React.Fragment>
      ))}
    </span>
  );
};

export default FreshnessStamp;
